import { Link } from "react-router-dom";
import { getStats } from "@/api/admin";
import { useAsync } from "@/lib/useAsync";
import { money } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Kpi } from "@/components/ui/kpi";
import { Notice } from "@/components/ui/feedback";
import { PageHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * What needs a person, first.
 *
 * The three counts across the top are QUEUES, not vanity metrics: an order held
 * for approval, an order the supplier has not answered about, and a payment
 * flagged for review are each a thing that stays broken until somebody opens
 * it. They are links, because a number an operator cannot act on is a number
 * they learn to skip past.
 *
 * The delivery and error counts are the last 24 HOURS and 30 days, rolling:
 * they answer "is the shop moving", which a window answers the same at any
 * hour. The money is the Stats screen's figure for the last 30 Istanbul days,
 * and its card opens that screen.
 */
/** A card that is a link: the whole card, with the focus ring on its edge. */
const CARD_LINK =
  "rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function DashboardScreen() {
  const { data, loading, error } = useAsync(() => getStats(), []);

  return (
    <div className="space-y-4">
      <PageHeader title="Dashboard" description="What is waiting, and what the last month looked like." />

      {error && <Notice tone="error">{error}</Notice>}

      {data?.supplierBalance && data.supplierBalance.cents < data.supplierBalanceAlarmCents ? (
        <Notice tone="error">
          Supplier balance is {money(data.supplierBalance.cents)}, below the{" "}
          {money(data.supplierBalanceAlarmCents)} you set. Top the eSIM Go account up: orders
          keep selling and delivery draws on this until it is empty.{" "}
          <span className="whitespace-nowrap">Read {age(data.supplierBalance.checkedAt)}.</span>
        </Notice>
      ) : null}

      {loading && !data ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[88px]" />
          ))}
        </div>
      ) : data ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <QueueCard
              to="/orders?status=on_hold"
              label="Held for approval"
              count={data.onHold}
              waiting="Paid, waiting for you"
              idle="Nothing waiting"
            />
            <QueueCard
              to="/orders?status=paid_pending_provision"
              label="Waiting on supplier"
              count={data.awaitingSupplier}
              waiting="Paid, not delivered"
              idle="Nothing stuck"
            />
            <QueueCard
              to="/payments?view=review"
              label="Payments to review"
              count={data.paymentsForReview}
              waiting="Parked for a person"
              idle="Nothing flagged"
            />
            <QueueCard
              to="/payments?view=refunds"
              label="Refunds due"
              count={data.refundsDue}
              waiting="Overpaid, surplus owed back"
              idle="Nothing owed"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label="Delivered, 24 hours" value={data.deliveredToday} />
            <Kpi label="Delivered, 30 days" value={data.deliveredMonth} />
            <Link to="/stats" className={CARD_LINK}>
              <Kpi
                label="Taken, 30 days"
                value={money(data.revenueMonthCents)}
                hint="Paid orders, before refunds · Stats"
              />
            </Link>
            <Kpi
              label="Supplier balance"
              value={data.supplierBalance ? money(data.supplierBalance.cents) : "not read yet"}
              tone={
                data.supplierBalance && data.supplierBalance.cents < data.supplierBalanceAlarmCents
                  ? "attention"
                  : undefined
              }
              hint={
                data.supplierBalance
                  ? `Read ${age(data.supplierBalance.checkedAt)} · shared with the app`
                  : "The hourly check has not run yet"
              }
            />
            <Kpi
              label="Errors, 24 hours"
              value={data.errorsToday}
              tone={data.errorsToday > 0 ? "attention" : undefined}
              hint={`${data.warningsToday} warnings`}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Orders by status</CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(data.ordersByStatus).length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">No orders yet.</p>
              ) : (
                <ul className="divide-y divide-border-light">
                  {Object.entries(data.ordersByStatus)
                    .sort((a, b) => b[1] - a[1])
                    .map(([status, count]) => (
                      <li key={status} className="flex items-center justify-between py-2 text-sm">
                        <Link
                          to={`/orders?status=${encodeURIComponent(status)}`}
                          className="rounded underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {status}
                        </Link>
                        <span className="nums font-medium">{count}</span>
                      </li>
                    ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}

/**
 * How long ago, in words.
 *
 * The balance is read on a schedule, not on load, so the figure above is
 * always a little old -- and this one is drawn down by the sibling app as well
 * as by this shop, so it can fall with no order here to explain it. A number
 * with no age on it would tell an operator they have room when they may not.
 */
function age(iso: string): string {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (!Number.isFinite(minutes) || minutes < 0) return "at an unknown time";
  if (minutes < 2) return "just now";
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? "an hour ago" : `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "a day ago" : `${days} days ago`;
}

/** A queue, which is a count that is also the way to the thing it counts. */
function QueueCard({
  to,
  label,
  count,
  waiting,
  idle,
}: {
  to: string;
  label: string;
  count: number;
  waiting: string;
  idle: string;
}) {
  return (
    <Link to={to} className={CARD_LINK}>
      <Kpi label={label} value={count} tone={count > 0 ? "attention" : undefined} hint={count > 0 ? waiting : idle} />
    </Link>
  );
}
